import React, { useState, useEffect } from 'react';
import { useNavigationUtils } from '../utils/navigationUtils';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  FolderOpen as FolderOpenIcon,
  Language as LanguageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import ProfileService from '../services/ProfileService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import { useAuth } from '../services/AuthService';
import { usePostHog } from '@posthog/react';
// import { completeJobSeekerSchema } from '../validation/onboardingSchema';
import LocationAutocomplete from '../components/LocationAutocomplete';
import DocumentUpload from '../components/DocumentUpload';
import DocumentService from '../services/DocumentService';
import {
  skillOptions,
  experienceOptions,
  availabilityOptions,
  salaryOptions,
  visaOptions,
  transportOptions,
  yesNoOptions,
  commonRequirements,
  skillRequirementsMap
} from '../constants/dropdownOptions';
import {
  englishLevelOptions,
  countryOptions,
  languageOptions,
} from '../constants/dropdownOptions';

// Skills list for display (using labels from centralized options)
const skills = skillOptions.map(option => option.label);

// FieldSet component (not used in current implementation)
// const FieldSet = ({ title, children }) => (
//   <Card sx={{ mb: 3 }}>
//     <CardContent>
//       <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
//         {title}
//       </Typography>
//       {children}
//     </CardContent>
//   </Card>
// );

const JobSeekerProfileManagement = () => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const { navigateBackToDashboard } = useNavigationUtils();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarDocId, setAvatarDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobSeekerProfileId, setJobSeekerProfileId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      userType: 'jobseeker',
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      jobseeker: {
        bioDescription: '',
        skills: [],
        labourRequirements: [],
        experience: '',
        availability: '',
        expectedSalary: '',
        visaStatus: '',
        location: '',
        suburb: '',
        postcode: '',
        state: '',
        lat: null,
        lng: null,
        transport: '',
        hasValidABN: '',
        abnNumber: '',
        countryOfOrigin: '',
        nativeLanguage: '',
        englishLevel: '',
      },
    }
  });

  const { reset, handleSubmit, formState: { errors } } = methods;
  const profileErrors = errors.profile || {};
  const jobseekerErrors = errors.jobseeker || {};
  const selectedSkills = methods.watch('jobseeker.skills') || [];
  const selectedLabourRequirements = methods.watch('jobseeker.labourRequirements') || [];
  const watchedHasValidABN = methods.watch('jobseeker.hasValidABN');

  // Get filtered labour requirements based on selected skills
  const getFilteredLabourRequirementsForSkills = (skills) => {
    if (skills.length === 0) {
      return commonRequirements;
    }
    
    const relevantRequirements = new Set(commonRequirements);
    
    skills.forEach(skill => {
      if (skillRequirementsMap[skill]) {
        skillRequirementsMap[skill].forEach(req => relevantRequirements.add(req));
      }
    });
    
    return Array.from(relevantRequirements).sort();
  };

  const getFilteredLabourRequirements = () => {
    return getFilteredLabourRequirementsForSkills(selectedSkills);
  };

  const filteredLabourRequirements = getFilteredLabourRequirements();

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        if (!user) {
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const userId = user?.sub;
        const profile = await new ProfileService().getRecordsByUserId(userId);
        const jobSeekerProfile = await new JobSeekerProfileService().getRecordsByUserId(userId);

        if (jobSeekerProfile?.id) {
          setJobSeekerProfileId(jobSeekerProfile.id);
        }

        const mergedData = {
          userType: 'jobseeker',
          profile: {
            firstName: profile?.firstName || user?.given_name || '',
            lastName: profile?.lastName || user?.family_name || '',
            email: profile?.email || user?.email || '',
            phone: profile?.phone || user?.phone_number || '',
          },
          jobseeker: {
            bioDescription: jobSeekerProfile?.bioDescription || '',
            skills: jobSeekerProfile?.skills || [],
            labourRequirements: jobSeekerProfile?.labourRequirements || [],
            experience: jobSeekerProfile?.experience || '',
            availability: jobSeekerProfile?.availability || '',
            expectedSalary: jobSeekerProfile?.expectedSalary || '',
            visaStatus: jobSeekerProfile?.visaStatus || '',
            location: jobSeekerProfile?.location || '',
            suburb: jobSeekerProfile?.suburb || '',
            postcode: jobSeekerProfile?.postcode || '',
            state: jobSeekerProfile?.state || '',
            lat: jobSeekerProfile?.lat ?? null,
            lng: jobSeekerProfile?.lng ?? null,
            transport: jobSeekerProfile?.transport || '',
            hasValidABN: jobSeekerProfile?.hasValidABN || '',
            abnNumber: jobSeekerProfile?.abnNumber || '',
            countryOfOrigin: jobSeekerProfile?.countryOfOrigin || '',
            nativeLanguage: jobSeekerProfile?.nativeLanguage || '',
            englishLevel: jobSeekerProfile?.englishLevel || '',
          },
        };

        reset(mergedData);

        // Load avatar preview if exists (via documents service)
        try {
          if (jobSeekerProfile?.id) {
            const res = await new DocumentService().getAvatarByProfileId(jobSeekerProfile.id);
            if (res && res.avatarUrl) {
              setAvatarPreviewUrl(res.avatarUrl);
              // fetch documents list separately later if needed
            }
          }
        } catch (err) {
          // ignore avatar load errors
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading profile data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [user, reset]);

  const handleSkillToggle = (skillLabel) => {
    // Find the skill value from the label (matching JobSeekerStep pattern)
    const skillOption = skillOptions.find(option => option.label === skillLabel);
    const skillValue = skillOption ? skillOption.value : skillLabel;
    
    const newSkills = selectedSkills.includes(skillValue)
      ? selectedSkills.filter((s) => s !== skillValue)
      : [...selectedSkills, skillValue];
    methods.setValue('jobseeker.skills', newSkills);
    
    // Clean up labour requirements that are no longer relevant
    const newFilteredRequirements = getFilteredLabourRequirementsForSkills(newSkills);
    const cleanedLabourRequirements = selectedLabourRequirements.filter(req => 
      newFilteredRequirements.includes(req)
    );
    methods.setValue('jobseeker.labourRequirements', cleanedLabourRequirements);
  };

  const handleLabourRequirementToggle = (requirement) => {
    const newRequirements = selectedLabourRequirements.includes(requirement)
      ? selectedLabourRequirements.filter((r) => r !== requirement)
      : [...selectedLabourRequirements, requirement];
    methods.setValue('jobseeker.labourRequirements', newRequirements);
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Basic validation: only images
    if (!file.type.startsWith('image/')) return;

    const preview = URL.createObjectURL(file);
    setSelectedAvatarFile(file);
    setAvatarPreviewUrl(preview);

    // If we don't have a persisted jobSeekerProfileId yet, just show preview and wait for save
    if (!jobSeekerProfileId) return;

    setAvatarLoading(true);
    try {
      const ds = new DocumentService();
      // Delete any existing avatar first (best-effort)
      try {
        await ds.deleteAvatarByProfileId(jobSeekerProfileId);
      } catch (err) {
        // ignore if no existing avatar or delete fails
      }

      // Upload new avatar immediately
      const uploaded = await ds.uploadDocument({
        jobSeekerProfileId,
        documentType: 'avatar',
        documentLabel: 'Avatar',
        file,
      });

      if (uploaded && uploaded.id) {
        setAvatarDocId(uploaded.id);
      }

      // Refresh avatar preview from server (presigned URL)
      try {
        const resp = await ds.getAvatarByProfileId(jobSeekerProfileId);
        setAvatarPreviewUrl(resp?.avatarUrl || preview);
      } catch (err) {
        // keep local preview if server refresh fails
        setAvatarPreviewUrl(preview);
      }

      setSnackbar({ open: true, message: 'Avatar updated', severity: 'success' });
    } catch (err) {
      console.error('Avatar change failed', err);
      setSnackbar({ open: true, message: 'Failed to update avatar', severity: 'error' });
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    const ok = window.confirm('Delete avatar? This will remove your profile photo.');
    if (!ok) return;

    setAvatarLoading(true);
    try {
      // Prefer server-side avatar delete by profile id (owner only)
      if (jobSeekerProfileId) {
        await new DocumentService().deleteAvatarByProfileId(jobSeekerProfileId);
      } else if (avatarDocId) {
        // Fallback to deleting by document id if we have it
        await new DocumentService().deleteDocument(avatarDocId);
      }

      // Refresh avatar from server to ensure consistent state
      try {
        if (jobSeekerProfileId) {
          const res = await new DocumentService().getAvatarByProfileId(jobSeekerProfileId);
          const newUrl = res?.avatarUrl || '';
          setAvatarPreviewUrl(newUrl);
        } else {
          setAvatarPreviewUrl('');
        }
      } catch (err) {
        setAvatarPreviewUrl('');
      }

      setSelectedAvatarFile(null);
      setAvatarDocId(null);
      setSnackbar({ open: true, message: 'Avatar removed', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete avatar', err);
      setSnackbar({ open: true, message: 'Failed to delete avatar', severity: 'error' });
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleLocationChange = (locationDetails) => {
    if (!locationDetails) {
      methods.setValue('jobseeker.location', '');
      methods.setValue('jobseeker.suburb', '');
      methods.setValue('jobseeker.postcode', '');
      methods.setValue('jobseeker.state', '');
      methods.setValue('jobseeker.lat', null);
      methods.setValue('jobseeker.lng', null);
      return;
    }

    methods.setValue('jobseeker.location', locationDetails.description);
    methods.setValue('jobseeker.suburb', locationDetails.suburb);
    methods.setValue('jobseeker.postcode', locationDetails.postcode);
    methods.setValue('jobseeker.state', locationDetails.state);
    methods.setValue('jobseeker.lat', locationDetails.lat);
    methods.setValue('jobseeker.lng', locationDetails.lng);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const userId = user?.sub;

      // Data is already split into logical structures — no mapping needed
      // First update the basic profile
      const basicProfile = await new ProfileService().createOrUpdateRecord({ userId, userType: data.userType, ...data.profile });

      // Then create/update the job seeker profile and capture the resulting id
      const jobSeekerResult = await new JobSeekerProfileService().createOrUpdateRecord({ userId, ...data.jobseeker });

      // If avatar file selected, upload it now. Backend will attach reference to job seeker profile.
      if (selectedAvatarFile) {
        setAvatarLoading(true);
        try {
          const uploaded = await new DocumentService().uploadDocument({
            jobSeekerProfileId: jobSeekerResult.id,
            documentType: 'avatar',
            documentLabel: 'Avatar',
            file: selectedAvatarFile,
          });

          // Save returned document id (if any) so we can delete by id if required
          if (uploaded && uploaded.id) {
            setAvatarDocId(uploaded.id);
          }

          // Refresh avatar preview from dedicated avatar endpoint
          const avatarResp = await new DocumentService().getAvatarByProfileId(jobSeekerResult.id);
          setAvatarPreviewUrl(avatarResp?.avatarUrl || '');
        } catch (err) {
          console.error('Avatar upload failed', err);
        } finally {
          setAvatarLoading(false);
        }
      }

      setSnackbar({
        open: true,
        message: 'Profile saved successfully!',
        severity: 'success'
      });
      posthog?.capture('profile_updated', { user_type: 'jobseeker', has_avatar: !!selectedAvatarFile });

    } catch (error) {
      console.error('Error saving profile:', error);
      setSnackbar({
        open: true,
        message: 'Error saving profile. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };



  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'left' }}>
      {/* Header */}
      <Box mb={5}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={navigateBackToDashboard}
          sx={{ 
            mb: 3, 
            color: 'text.secondary',
            '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
          }}
        >
          Back to Dashboard
        </Button>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          fontWeight="800"
          sx={{ 
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
            color: 'primary.contrastText', 
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Manage Your Profile
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          Keep your information up to date to attract the right opportunities
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          {/* Basic Information */}
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={4}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50] }}>
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" fontWeight="600">
                    Basic Information
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Avatar
                          src={avatarPreviewUrl || undefined}
                          sx={{ width: 96, height: 96, fontSize: 32, bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50] }}
                        >
                          {!avatarPreviewUrl && (methods.getValues('profile.firstName') || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Tooltip title={avatarPreviewUrl ? 'Remove photo' : 'Remove photo'}>
                          <IconButton
                            size="small"
                            onClick={handleDeleteAvatar}
                            disabled={avatarLoading}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              '&:hover': { bgcolor: 'error.main', color: 'white' },
                              boxShadow: 1,
                              width: 34,
                              height: 34,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box>
                        <Button variant="outlined" component="label">
                          {selectedAvatarFile ? 'Change Photo' : 'Upload Photo'}
                          <input hidden accept="image/*" type="file" onChange={handleAvatarSelect} />
                        </Button>
                        {avatarLoading && <Typography variant="caption" sx={{ ml: 2 }}>Uploading...</Typography>}
                        {selectedAvatarFile && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{selectedAvatarFile.name}</Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Recommended size: 400x400px. JPG/PNG only.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="profile.firstName"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="First Name"
                          fullWidth
                          required
                          sx={{ bgcolor: 'background.input' }}
                          error={!!profileErrors.firstName}
                          helperText={profileErrors.firstName?.message}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="profile.lastName"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Name"
                          fullWidth
                          required
                          sx={{ bgcolor: 'background.input' }}
                          error={!!profileErrors.lastName}
                          helperText={profileErrors.lastName?.message}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="profile.email"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email Address"
                          type="email"
                          fullWidth
                          required
                          sx={{ bgcolor: 'background.input' }}
                          InputProps={{
                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                          error={!!profileErrors.email}
                          helperText={profileErrors.email?.message}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="profile.phone"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Phone Number"
                          fullWidth
                          required
                          sx={{ bgcolor: 'background.input' }}
                          InputProps={{
                            startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          }}
                          error={!!profileErrors.phone}
                          helperText={profileErrors.phone?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="jobseeker.bioDescription"
                      render={({ field }) => {
                        const { onChange, value, ...rest } = field;
                        const handleBioChange = (e) => {
                          // Strip any HTML tags and limit to 500 characters
                          let v = e.target.value.replace(/<[^>]*>/g, '');
                          if (v.length > 500) v = v.slice(0, 500);
                          onChange(v);
                        };
                        return (
                          <TextField
                            {...rest}
                            value={value || ''}
                            onChange={handleBioChange}
                            label="Bio / About you"
                            fullWidth
                            multiline
                            rows={4}
                            sx={{ bgcolor: 'background.input' }}
                            placeholder="Write a short bio that will appear on your public profile. (Max 500 characters, plain text)"
                            error={!!jobseekerErrors.bioDescription}
                            helperText={jobseekerErrors.bioDescription?.message || `${(value || '').length}/500 characters`}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          {/* Work Profile */}
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={4}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50] }}>
                    <WorkIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" fontWeight="600">
                    Work Profile
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  {/* Skills */}
                  <Grid size={12}>
                    <FormControl fullWidth error={!!jobseekerErrors.skills}>
                      <InputLabel shrink>Skills & Expertise *</InputLabel>
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {skills.map((skillLabel) => {
                          const skillOption = skillOptions.find(option => option.label === skillLabel);
                          const skillValue = skillOption ? skillOption.value : skillLabel;
                          
                          return (
                          <Chip
                            key={skillLabel}
                            label={skillLabel}
                            onClick={() => handleSkillToggle(skillLabel)}
                            sx={{
                              bgcolor: selectedSkills.includes(skillValue) ? 'primary.main' : 'transparent',
                              color: selectedSkills.includes(skillValue) ? 'primary.contrastText' : 'text.primary',
                              border: '1px solid',
                              borderColor: selectedSkills.includes(skillValue) ? 'primary.main' : 'divider',
                              fontSize: '14px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': { 
                                transform: 'scale(1.05)',
                                bgcolor: selectedSkills.includes(skillValue) ? 'primary.dark' : 'action.hover'
                              },
                              padding: 2,
                            }}
                          />
                          );
                        })}
                      </Box>
                      {jobseekerErrors.skills && (
                        <FormHelperText>{jobseekerErrors.skills.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* General Labour Requirements */}
                  <Grid size={12}>
                    <FormControl fullWidth error={!!jobseekerErrors.labourRequirements}>
                      <InputLabel shrink>General Labour Requirements</InputLabel>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        Select all certifications, licenses, and requirements you have
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                         {filteredLabourRequirements.map((requirement) => (
                           <Chip
                             key={requirement}
                             label={requirement}
                             onClick={() => handleLabourRequirementToggle(requirement)}
                             sx={{
                               bgcolor: selectedLabourRequirements.includes(requirement) ? 'secondary.main' : 'transparent',
                               color: selectedLabourRequirements.includes(requirement) ? 'secondary.contrastText' : 'text.primary',
                               border: '1px solid',
                               borderColor: selectedLabourRequirements.includes(requirement) ? 'secondary.main' : 'divider',
                               fontSize: '13px',
                               cursor: 'pointer',
                               transition: 'all 0.2s',
                               '&:hover': { transform: 'scale(1.05)' },
                               padding: 1.5,
                             }}
                           />
                         ))}
                      </Box>
                      {jobseekerErrors.labourRequirements && (
                        <FormHelperText>{jobseekerErrors.labourRequirements.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Experience Level */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.experience}>
                      <InputLabel>Experience Level *</InputLabel>
                      <Controller
                        name="jobseeker.experience"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="Experience Level *">
                            {experienceOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.experience && (
                        <FormHelperText>{jobseekerErrors.experience.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Availability */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.availability}>
                      <InputLabel>Availability *</InputLabel>
                      <Controller
                        name="jobseeker.availability"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="Availability *">
                            {availabilityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.availability && (
                        <FormHelperText>{jobseekerErrors.availability.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Expected Salary */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.expectedSalary}>
                      <InputLabel>Expected Hourly Rate *</InputLabel>
                      <Controller
                        name="jobseeker.expectedSalary"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="Expected Hourly Rate *">
                            {salaryOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.expectedSalary && (
                        <FormHelperText>{jobseekerErrors.expectedSalary.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Visa Status */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.visaStatus}>
                      <InputLabel>Visa Status *</InputLabel>
                      <Controller
                        name="jobseeker.visaStatus"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="Visa Status *">
                            {visaOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.visaStatus && (
                        <FormHelperText>{jobseekerErrors.visaStatus.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Location */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.location}>
                      <Box sx={{ bgcolor: 'background.input', borderRadius: 1 }}>
                        <LocationAutocomplete 
                          onLocationSelect={handleLocationChange} 
                          initialValue={methods.watch('jobseeker.location')}
                          required
                        />
                      </Box>
                      {jobseekerErrors.location && (
                        <FormHelperText>{jobseekerErrors.location.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Transport */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.transport}>
                      <InputLabel>Transport *</InputLabel>
                      <Controller
                        name="jobseeker.transport"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="Transport *">
                            {transportOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.transport && (
                        <FormHelperText>{jobseekerErrors.transport.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* ABN Status */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.hasValidABN}>
                      <InputLabel>Do you have a valid ABN? *</InputLabel>
                      <Controller
                        name="jobseeker.hasValidABN"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="Do you have a valid ABN? *">
                            {yesNoOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.hasValidABN && (
                        <FormHelperText>{jobseekerErrors.hasValidABN.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* ABN Number (conditional) */}
                  {watchedHasValidABN === 'yes' && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="jobseeker.abnNumber"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="ABN Number"
                            fullWidth
                            sx={{ bgcolor: 'background.input' }}
                            placeholder="Enter your ABN number"
                            error={!!jobseekerErrors.abnNumber}
                            helperText={jobseekerErrors.abnNumber?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          {/* Language & Background */}
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={4}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50] }}>
                    <LanguageIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" fontWeight="600">
                    Language & Background
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="jobseeker.countryOfOrigin"
                      render={({ field: { onChange, value, ...field } }) => (
                        <Autocomplete
                          {...field}
                          value={value || null}
                          onChange={(_, newValue) => onChange(newValue || '')}
                          options={countryOptions}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Country of Origin"
                              sx={{ bgcolor: 'background.input' }}
                              placeholder="Start typing…"
                              error={!!jobseekerErrors.countryOfOrigin}
                              helperText={jobseekerErrors.countryOfOrigin?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="jobseeker.nativeLanguage"
                      render={({ field: { onChange, value, ...field } }) => (
                        <Autocomplete
                          {...field}
                          value={value || null}
                          onChange={(_, newValue) => onChange(newValue || '')}
                          options={languageOptions}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Native Language"
                              sx={{ bgcolor: 'background.input' }}
                              placeholder="Start typing…"
                              error={!!jobseekerErrors.nativeLanguage}
                              helperText={jobseekerErrors.nativeLanguage?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth error={!!jobseekerErrors.englishLevel}>
                      <InputLabel>English Level</InputLabel>
                      <Controller
                        name="jobseeker.englishLevel"
                        render={({ field }) => (
                          <Select sx={{ bgcolor: 'background.input' }} {...field} label="English Level">
                            {englishLevelOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {jobseekerErrors.englishLevel && (
                        <FormHelperText>{jobseekerErrors.englishLevel.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          {/* Documents Upload */}
          <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={4}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50] }}>
                    <FolderOpenIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" fontWeight="600">
                    Documents
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Upload your ID photo, White Card, tickets, licenses, and certificates to strengthen your profile.
                </Typography>
                {jobSeekerProfileId ? (
                  <DocumentUpload jobSeekerProfileId={jobSeekerProfileId} />
                ) : (
                  <Alert severity="info">
                    Save your profile first to enable document uploads.
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Box display="flex" justifyContent="space-between" gap={2} sx={{ mt: 2, pb: 4 }}>
            <Button
              variant="outlined"
              onClick={navigateBackToDashboard}
              startIcon={<ArrowBackIcon />}
              disabled={loading}
              sx={{ 
                color: 'text.secondary', 
                borderColor: 'divider', 
                '&:hover': { 
                  borderColor: 'text.primary', 
                  bgcolor: 'action.hover',
                  color: 'text.primary'
                } 
              }}
            >
              Back to Dashboard
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={loading}
              size="large"
              sx={{
                bgcolor: 'primary.main',
                px: 5,
                py: 1.5,
                borderRadius: 3,
                boxShadow: (theme) => `0 4px 14px 0 ${theme.palette.primary.main}63`,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: (theme) => `0 6px 20px 0 ${theme.palette.primary.main}3b`,
                }
              }}
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </Box>
        </Stack>
      </form>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </FormProvider>
  );
};

export default JobSeekerProfileManagement;
